// scheduling/components/pool/PoolNotesEditor.jsx
// Edit a waiting pool job's notes in place (Job Pools, Trello #257).
//
// The requirements doc says notes are "editable after the fact by the manager". Shared
// by both surfaces that offer it: the Job Pools card (fix it where you read it) and the
// dashboard Pool tab (fix it where you track what you sent).
//
// WAITING ONLY, and that's a real constraint rather than tidiness: the note is read at
// claim time and becomes the body of PE's forward email. Once claimed, that email has
// gone out, so a later edit would leave RPRS claiming something the recipient never
// received. The server enforces it too — this just avoids offering a control that
// would 400.
//
// 500 chars to match rprs.PoolJobs.Notes. Note PE truncates at 480 when it composes
// the email (NOTES_MAX in peJobs.js) since the attribution sentence goes first, so the
// counter warns past 480 rather than only at the hard limit.

import { useEffect, useRef, useState } from 'react';
import { updatePoolNotes } from '../../services/poolApi';

const MAX = 500;
const PE_SAFE = 480;   // beyond this, PE's email may clip the tail — see NOTES_MAX

export default function PoolNotesEditor({ poolJobId, notes, onSaved, onCancel, autoFocus = true }) {
  const [text, setText] = useState(notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.focus();
      // Caret to the end rather than selecting everything — an edit is usually an
      // addition to an existing note, not a replacement.
      const n = ref.current.value.length;
      ref.current.setSelectionRange(n, n);
    }
  }, [autoFocus]);

  const dirty = (text || '') !== (notes || '');

  async function save() {
    if (!dirty) { onCancel?.(); return; }
    setSaving(true); setError(null);
    try {
      const res = await updatePoolNotes({ poolJobId, notes: text });
      onSaved?.(res.notes ?? text ?? '');
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  }

  return (
    <div className="sched-pool-notes-edit" onClick={(e) => e.stopPropagation()}>
      <textarea
        ref={ref}
        className="sched-pool-notes-input"
        rows={3}
        maxLength={MAX}
        value={text}
        disabled={saving}
        placeholder="Context for whoever picks this up — what's done, what to watch for…"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Enter saves, Shift+Enter for a newline, Escape abandons. Matches the
          // muscle memory of every other inline editor in the app.
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel?.(); }
        }}
      />
      <div className="sched-pool-notes-edit-foot">
        <button type="button" className="sched-mgr-modal-btn sched-pool-notes-save"
          onClick={save} disabled={saving}>
          {saving ? 'Saving…' : dirty ? 'Save' : 'Done'}
        </button>
        <button type="button" className="sched-pool-notes-cancel"
          onClick={() => onCancel?.()} disabled={saving}>
          Cancel
        </button>
        <span className={`sched-pool-notes-count${text.length > PE_SAFE ? ' sched-pool-notes-count--warn' : ''}`}>
          {text.length}/{MAX}
          {text.length > PE_SAFE ? ' · PE email may clip past 480' : ''}
        </span>
        {error && <span className="sched-pool-notes-err">{error}</span>}
      </div>
      <div className="sched-pool-notes-hint">
        Whoever claims this job gets these notes in their Practice Engine email.
      </div>
    </div>
  );
}
